/* =========================================================
   LUCA Outfit Check
   JS Version: v1.4.8 (STABLE)
   - LUCA list (tag outfitcheck) + variants
   - Click LUCA preview -> go to product
   - Desktop arrows for image navigation
   - Mobile swipe for image navigation (keeps vertical scroll)
   - iOS paste sheet
   - AutoCrop 2.0 per image (uploads/paste only)
   - Fit toggle Apple-like
   ========================================================= */

console.log("LUCA Outfit Check v1.4.8 loaded");

(() => {
  const STORAGE_KEY = "loc_web_state_v148";
  const LUCA_CACHE_KEY = "loc_luca_products_cache_v148";
  const MAX_PER_SLOT = 5;
  const EMOJI = ["👕", "👖", "👟"];

  const $ = (id) => document.getElementById(id);
  const clamp = (n, a, b) => Math.min(Math.max(n, a), b);

  let state = {
    activeSlot: 0,
    ui: { fitEnabled: false, slotHeightPx: null },
    luca: { selected: null },
    categories: [
      { name: "Oberteil", items: [], active: 0 },
      { name: "Hose", items: [], active: 0 },
      { name: "Schuhe", items: [], active: 0 },
    ],
  };

  /* -------------------- State -------------------- */
  function saveState() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {}
  }

  function loadState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) state = Object.assign(state, JSON.parse(raw));
    } catch (e) {}
    normalizeState();
  }

  function normalizeState() {
    if (!state.ui) state.ui = { fitEnabled: false, slotHeightPx: null };
    if (!state.luca) state.luca = { selected: null };

    if (!Array.isArray(state.categories) || state.categories.length !== 3) {
      state.categories = [
        { name: "Oberteil", items: [], active: 0 },
        { name: "Hose", items: [], active: 0 },
        { name: "Schuhe", items: [], active: 0 },
      ];
    }

    state.categories.forEach((c) => {
      if (!Array.isArray(c.items)) c.items = [];
      c.items = c.items.slice(0, MAX_PER_SLOT);
      c.active = clamp(c.active || 0, 0, Math.max(0, c.items.length - 1));
      c.items.forEach((it) => {
        if (typeof it !== "object" || it === null) return;
        if (!("name" in it)) it.name = "Bild";
        if (!("snapshot" in it)) it.snapshot = null;
        if (!("url" in it)) it.url = null;
        if (!("useCrop" in it)) it.useCrop = false;
        if (!("cropSnapshot" in it)) it.cropSnapshot = null;
      });
    });

    state.activeSlot = clamp(state.activeSlot || 0, 0, 2);
  }

  /* -------------------- Fit toggle -------------------- */
  function computeFitHeightPx() {
    return Math.max(220, Math.round(window.innerHeight / 3.2));
  }

  function applySlotHeight() {
    const px = state.ui.fitEnabled ? (state.ui.slotHeightPx || computeFitHeightPx()) : null;
    document.documentElement.style.setProperty("--soc-slot-h", px ? `${px}px` : "auto");
  }

  function updateFitBtn() {
    const btn = $("soc-fit");
    if (!btn) return;
    if (state.ui.fitEnabled) {
      btn.classList.add("soc-toggle-on");
      btn.setAttribute("aria-pressed", "true");
      btn.textContent = "✓ Fit aktiv";
    } else {
      btn.classList.remove("soc-toggle-on");
      btn.setAttribute("aria-pressed", "false");
      btn.textContent = "Fit auf Display";
    }
  }

  function toggleFit() {
    state.ui.fitEnabled = !state.ui.fitEnabled;
    state.ui.slotHeightPx = state.ui.fitEnabled ? computeFitHeightPx() : null;
    saveState();
    renderSlots();
    updateFitBtn();
  }

  /* -------------------- Item creation -------------------- */
  function createItemFromFile(file) {
    return new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () =>
        resolve({
          name: file.name || "Bild",
          snapshot: String(r.result),
          url: null,
          useCrop: false,
          cropSnapshot: null,
        });
      r.readAsDataURL(file);
    });
  }

  async function createItemFromURL(url) {
    return {
      name: (url.split("/").pop() || "Bild").split("?")[0],
      url,
      snapshot: null,
      useCrop: false,
      cropSnapshot: null,
    };
  }

  function addItemToActiveSlot(item) {
    const cat = state.categories[state.activeSlot];
    if (cat.items.length >= MAX_PER_SLOT) {
      alert("Maximal 5 Bilder pro Slot.");
      return;
    }
    cat.items.push(item);
    cat.active = cat.items.length - 1;
    saveState();
    renderSlots();
  }

  /* -------------------- AutoCrop 2.0 -------------------- */
  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("img_load_failed"));
      img.src = src;
    });
  }

  function rowEnergy(data, w, y) {
    let e = 0;
    const off = y * w * 4;
    for (let x = 0; x < w - 1; x++) {
      const i = off + x * 4;
      const l1 = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
      const l2 = 0.2126 * data[i + 4] + 0.7152 * data[i + 5] + 0.0722 * data[i + 6];
      e += Math.abs(l2 - l1);
    }
    return e / (w - 1);
  }

async function autoCropTopBottom(dataURL) {
  try {
    const img = await loadImage(dataURL);
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (w < 80 || h < 80) return null;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    canvas.width = w;
    canvas.height = h;
    ctx.drawImage(img, 0, 0);

    const { data } = ctx.getImageData(0, 0, w, h);

    // ---------- helpers: sRGB -> Lab (D65) ----------
    function srgbToLinear(c) {
      c /= 255;
      return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    }
    function rgbToXyz(r, g, b) {
      r = srgbToLinear(r); g = srgbToLinear(g); b = srgbToLinear(b);
      // sRGB D65
      const x = r * 0.4124 + g * 0.3576 + b * 0.1805;
      const y = r * 0.2126 + g * 0.7152 + b * 0.0722;
      const z = r * 0.0193 + g * 0.1192 + b * 0.9505;
      return [x, y, z];
    }
    function f(t) {
      return t > 0.008856 ? Math.cbrt(t) : (7.787 * t + 16 / 116);
    }
    function rgbToLab(r, g, b) {
      const [x, y, z] = rgbToXyz(r, g, b);
      // reference white D65
      const xr = x / 0.95047;
      const yr = y / 1.00000;
      const zr = z / 1.08883;
      const fx = f(xr), fy = f(yr), fz = f(zr);
      const L = 116 * fy - 16;
      const a = 500 * (fx - fy);
      const b2 = 200 * (fy - fz);
      return [L, a, b2];
    }
    function deltaE76(lab1, lab2) {
      const dL = lab1[0] - lab2[0];
      const da = lab1[1] - lab2[1];
      const db = lab1[2] - lab2[2];
      return Math.sqrt(dL * dL + da * da + db * db);
    }

    // ---------- 1) estimate background Lab from 4 corner patches ----------
    const patchW = Math.max(10, Math.round(w * 0.06));
    const patchH = Math.max(10, Math.round(h * 0.06));
    const stepX = Math.max(1, Math.round(patchW / 18));
    const stepY = Math.max(1, Math.round(patchH / 18));

    const labs = [];
    function samplePatch(x0, y0) {
      for (let y = y0; y < y0 + patchH; y += stepY) {
        for (let x = x0; x < x0 + patchW; x += stepX) {
          const i = (y * w + x) * 4;
          labs.push(rgbToLab(data[i], data[i + 1], data[i + 2]));
        }
      }
    }
    samplePatch(0, 0);
    samplePatch(w - patchW, 0);
    samplePatch(0, h - patchH);
    samplePatch(w - patchW, h - patchH);

    function median(vals) {
      const a = vals.slice().sort((x, y) => x - y);
      return a[Math.floor(a.length / 2)];
    }
    const Ls = labs.map(v => v[0]), As = labs.map(v => v[1]), Bs = labs.map(v => v[2]);
    const bgLab = [median(Ls), median(As), median(Bs)];

    // ---------- 2) scan for foreground pixels (downsampled) and get bounding box ----------
    // downsample for performance
    const xStep = Math.max(1, Math.round(w / 320));
    const yStep = Math.max(1, Math.round(h / 320));

    // adaptive threshold:
    // compute bg spread as robust proxy (median absolute deviation-ish)
    const dEs = labs.map(l => deltaE76(l, bgLab)).sort((a,b)=>a-b);
    const dMed = dEs[Math.floor(dEs.length * 0.5)] || 0;
    const dP90 = dEs[Math.floor(dEs.length * 0.9)] || 0;

    // If background is noisy, raise threshold slightly; if super clean, keep it sensitive.
    const base = 9; // sensitive for low-contrast shirts
    const thr = base + Math.min(6, (dP90 - dMed) * 0.25);

    let minY = h, maxY = 0;
    let found = false;

    // (We only need top/bottom; still easiest via bbox on Y)
    for (let y = 0; y < h; y += yStep) {
      for (let x = 0; x < w; x += xStep) {
        const i = (y * w + x) * 4;
        const lab = rgbToLab(data[i], data[i + 1], data[i + 2]);
        const dE = deltaE76(lab, bgLab);
        if (dE > thr) {
          found = true;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

if (!found) {
  const simple = simpleTopBottomScan(data, w, h, bgLab);
  if (!simple) return null;
  minY = simple.minY;
  maxY = simple.maxY;
}

    // Expand bbox a bit because we downsampled
    minY = Math.max(0, minY - yStep * 2);
    maxY = Math.min(h - 1, maxY + yStep * 2);

    // ---------- 3) padding/safety (protect collar) ----------
    const padTop = Math.round(h * 0.08);     // strong top protection for collars
    const padBottom = Math.round(h * 0.03);  // smaller bottom padding

    let top = Math.max(0, minY - padTop);
    let bottom = Math.min(h, maxY + padBottom);

    // don't allow extreme crops
    if (bottom - top < Math.round(h * 0.55)) return null;

    // ---------- 4) output ----------
    const out = document.createElement("canvas");
    out.width = w;
    out.height = bottom - top;
    out.getContext("2d").drawImage(canvas, 0, top, w, bottom - top, 0, 0, w, bottom - top);

    return out.toDataURL("image/jpeg", 0.88);
  } catch (e) {
    return null;
  }
}

function simpleTopBottomScan(data, w, h, bgLab) {
  // super simple: if ANY pixel differs from corner-bg -> foreground
  // tuned for low-contrast / very bright sneakers
  const xStep = Math.max(1, Math.round(w / 220));
  const yStep = Math.max(1, Math.round(h / 320));

  // Convert bgLab back to an "approx" rgb distance proxy is hard,
  // so we use a lightweight RGB check against bg estimated from corners.
  // We'll approximate bg RGB by sampling a few corner pixels again here.
  const cornerSamples = [];
  function pushRGB(x, y) {
    const i = (y * w + x) * 4;
    cornerSamples.push([data[i], data[i+1], data[i+2]]);
  }
  const px = Math.max(2, Math.round(w * 0.03));
  const py = Math.max(2, Math.round(h * 0.03));
  pushRGB(2,2); pushRGB(px,py);
  pushRGB(w-3,2); pushRGB(w-px,py);
  pushRGB(2,h-3); pushRGB(px,h-py);
  pushRGB(w-3,h-3); pushRGB(w-px,h-py);

  // median bg rgb
  const rs = cornerSamples.map(v=>v[0]).sort((a,b)=>a-b);
  const gs = cornerSamples.map(v=>v[1]).sort((a,b)=>a-b);
  const bs = cornerSamples.map(v=>v[2]).sort((a,b)=>a-b);
  const bg = [rs[Math.floor(rs.length/2)], gs[Math.floor(gs.length/2)], bs[Math.floor(bs.length/2)]];

  // low-contrast-friendly threshold
  const thr = 10; // if needed: 8 (more sensitive) / 14 (less sensitive)

  function rowHasFg(y) {
    for (let x = 0; x < w; x += xStep) {
      const i = (y * w + x) * 4;
      const dr = Math.abs(data[i]   - bg[0]);
      const dg = Math.abs(data[i+1] - bg[1]);
      const db = Math.abs(data[i+2] - bg[2]);
      // "any channel differs enough" OR combined differs
      if (dr > thr || dg > thr || db > thr || (dr + dg + db) > thr * 2.2) return true;
    }
    return false;
  }

  // find top
  let top = -1;
  for (let y = 0; y < h; y += yStep) {
    if (rowHasFg(y)) { top = y; break; }
  }
  if (top === -1) return null;

  // find bottom
  let bottom = -1;
  for (let y = h - 1; y >= 0; y -= yStep) {
    if (rowHasFg(y)) { bottom = y; break; }
  }
  if (bottom === -1 || bottom <= top) return null;

  // safety padding
  const padTop = Math.round(h * 0.08);
  const padBottom = Math.round(h * 0.03);

  return {
    minY: Math.max(0, top - padTop),
    maxY: Math.min(h - 1, bottom + padBottom),
  };
}

/* Fallback: previous energy-based crop, but slightly safer */
async function autoCropTopBottom_energyFallback(canvas, w, h) {
  try {
    const ctx = canvas.getContext("2d", { willReadFrequently:true });
    const imgData = ctx.getImageData(0,0,w,h);
    const data = imgData.data;

    const step = Math.max(1, Math.round(h / 220));
    const energies = [];

    function rowEnergy(y){
      let e = 0;
      const off = y * w * 4;
      for(let x=0; x<w-1; x++){
        const i = off + x*4;
        const l1 = 0.2126*data[i] + 0.7152*data[i+1] + 0.0722*data[i+2];
        const l2 = 0.2126*data[i+4] + 0.7152*data[i+5] + 0.0722*data[i+6];
        e += Math.abs(l2-l1);
      }
      return e/(w-1);
    }

    for(let y=0; y<h; y+=step) energies.push(rowEnergy(y));

    const mean = energies.reduce((a,b)=>a+b,0)/energies.length;
    const threshold = mean * 1.12; // less strict than before

    let topIdx=0; while(topIdx<energies.length && energies[topIdx]<threshold) topIdx++;
    let botIdx=energies.length-1; while(botIdx>0 && energies[botIdx]<threshold) botIdx--;

    const pad = Math.round(h*0.04); // safer padding
    let top = Math.max(0, topIdx*step - pad);
    let bottom = Math.min(h, botIdx*step + pad);

    if (bottom - top < Math.round(h*0.55)) return null;

    const out = document.createElement("canvas");
    out.width = w;
    out.height = bottom - top;
    out.getContext("2d").drawImage(canvas, 0, top, w, bottom-top, 0, 0, w, bottom-top);
    return out.toDataURL("image/jpeg", 0.88);
  } catch(e){
    return null;
  }
}


/* Fallback: previous energy-based crop, but slightly safer */
async function autoCropTopBottom_energyFallback(canvas, w, h) {
  try {
    const ctx = canvas.getContext("2d", { willReadFrequently:true });
    const imgData = ctx.getImageData(0,0,w,h);
    const data = imgData.data;

    const step = Math.max(1, Math.round(h / 220));
    const energies = [];

    function rowEnergy(y){
      let e = 0;
      const off = y * w * 4;
      for(let x=0; x<w-1; x++){
        const i = off + x*4;
        const l1 = 0.2126*data[i] + 0.7152*data[i+1] + 0.0722*data[i+2];
        const l2 = 0.2126*data[i+4] + 0.7152*data[i+5] + 0.0722*data[i+6];
        e += Math.abs(l2-l1);
      }
      return e/(w-1);
    }

    for(let y=0; y<h; y+=step) energies.push(rowEnergy(y));

    const mean = energies.reduce((a,b)=>a+b,0)/energies.length;
    const threshold = mean * 1.12; // less strict than before

    let topIdx=0; while(topIdx<energies.length && energies[topIdx]<threshold) topIdx++;
    let botIdx=energies.length-1; while(botIdx>0 && energies[botIdx]<threshold) botIdx--;

    const pad = Math.round(h*0.04); // safer padding
    let top = Math.max(0, topIdx*step - pad);
    let bottom = Math.min(h, botIdx*step + pad);

    if (bottom - top < Math.round(h*0.55)) return null;

    const out = document.createElement("canvas");
    out.width = w;
    out.height = bottom - top;
    out.getContext("2d").drawImage(canvas, 0, top, w, bottom-top, 0, 0, w, bottom-top);
    return out.toDataURL("image/jpeg", 0.88);
  } catch(e){
    return null;
  }
}


  async function ensureCropped(item) {
    if (item.cropSnapshot) return item.cropSnapshot;

    // only uploads/paste (data URLs) are safe for canvas
    if (!item.snapshot || !String(item.snapshot).startsWith("data:image/")) return null;

    const cropped = await autoCropTopBottom(item.snapshot);
    if (cropped) {
      item.cropSnapshot = cropped;
      saveState();
      return cropped;
    }
    return null;
  }

  /* -------------------- LUCA list (tag outfitcheck) -------------------- */
  let lucaItems = [];

  async function fetchLucaVariants() {
    // cached 6h
    try {
      const cached = JSON.parse(localStorage.getItem(LUCA_CACHE_KEY) || "null");
      if (cached && cached.t && Date.now() - cached.t < 6 * 60 * 60 * 1000 && Array.isArray(cached.items)) {
        return cached.items;
      }
    } catch (e) {}

    try {
      const res = await fetch("/collections/all/products.json?limit=250", { credentials: "same-origin" });
      if (!res.ok) throw new Error("products.json not ok");
      const data = await res.json();

      const out = [];
      for (const p of data.products || []) {
        const tagsRaw = p.tags ?? "";
        const tags = Array.isArray(tagsRaw)
          ? tagsRaw.map((t) => String(t).trim().toLowerCase())
          : String(tagsRaw).split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);

        if (!tags.includes("outfitcheck")) continue;

        const imgs = (p.images || []).map((i) => i?.src).filter(Boolean);
        const fallback = imgs[0] || (p.image && p.image.src) || null;

        for (const v of p.variants || []) {
          const vImg = (v.featured_image && (v.featured_image.src || v.featured_image)) || fallback;
          if (!vImg) continue;

          const label = (v.title && v.title !== "Default Title") ? `${p.title} – ${v.title}` : p.title;

          out.push({
            handle: p.handle,
            label,
            image: vImg,
            variantId: v.id || null,
          });
        }
      }

      try {
        localStorage.setItem(LUCA_CACHE_KEY, JSON.stringify({ t: Date.now(), items: out }));
      } catch (e) {}

      return out;
    } catch (e) {
      console.warn("LUCA fetch failed:", e);
      return [];
    }
  }

  function renderLucaList() {
    const root = $("soc-luca-results");
    if (!root) return;

    root.innerHTML = "";
    if (!lucaItems.length) {
      const div = document.createElement("div");
      div.style.color = "rgba(17,17,17,.55)";
      div.style.fontSize = "12px";
      div.style.padding = "6px 2px";
      div.textContent = "Keine LUCA Varianten mit Tag 'outfitcheck' gefunden.";
      root.appendChild(div);
      return;
    }

    lucaItems.slice(0, 120).forEach((it) => {
      const row = document.createElement("div");
      row.className = "soc-lp-item";

      const img = document.createElement("img");
      img.className = "soc-lp-thumb";
      img.src = it.image;
      img.alt = it.label;

      const name = document.createElement("div");
      name.className = "soc-lp-name";
      name.textContent = it.label;

      row.append(img, name);
      row.addEventListener("click", () => {
        state.luca.selected = it;
        saveState();
        renderLucaPreview();
      });

      root.appendChild(row);
    });
  }

  function renderLucaPreview() {
    const preview = $("soc-luca-preview");
    const link = $("soc-luca-link");
    if (!preview) return;

    preview.innerHTML = "";

    const sel = state.luca.selected;
    if (!sel) {
      const empty = document.createElement("div");
      empty.className = "soc-empty";
      empty.textContent = "Optional: LUCA Etui auswählen";
      preview.appendChild(empty);
      if (link) link.style.display = "none";
      return;
    }

    const img = document.createElement("img");
    img.src = sel.image;
    img.alt = sel.label || "LUCA Etui";
    img.style.cursor = "pointer";

    img.addEventListener("click", () => {
      const url = "/products/" + sel.handle + (sel.variantId ? ("?variant=" + sel.variantId) : "");
      window.location.href = url;
    });

    preview.appendChild(img);

    if (link) {
      link.href = "/products/" + sel.handle + (sel.variantId ? ("?variant=" + sel.variantId) : "");
      link.textContent = "Zum Produkt";
      link.style.display = "";
    }
  }

  /* -------------------- Slot rendering -------------------- */
  function buildSlot(cat, idx) {
    const slot = document.createElement("div");
    slot.className = "soc-slot" + (idx === state.activeSlot ? " soc-active-slot" : "");

    const ui = document.createElement("div");
    ui.className = "soc-slot-ui";

    const badge = document.createElement("div");
    badge.className = "soc-slot-badge";
    badge.textContent = `${cat.items.length ? (cat.active + 1) : 0}/${MAX_PER_SLOT}`;

    const cropBtn = document.createElement("button");
    cropBtn.className = "soc-slot-btn";
    cropBtn.type = "button";
    cropBtn.title = "AutoCrop (oben/unten)";
    cropBtn.textContent = "✂️";

    const trashBtn = document.createElement("button");
    trashBtn.className = "soc-slot-btn";
    trashBtn.type = "button";
    trashBtn.title = "Bild löschen";
    trashBtn.textContent = "🗑️";

    const btnCol = document.createElement("div");
    btnCol.className = "soc-slot-actions";
    btnCol.append(cropBtn, trashBtn);

    ui.append(badge, btnCol);
    slot.appendChild(ui);

    const cur = cat.items[cat.active];

    // --- Dynamic slot resize when AutoCrop is active (and Fit is OFF) ---
if (!state.ui.fitEnabled && cur && cur.useCrop) {
  const src = cur.cropSnapshot || cur.snapshot;

  if (src && String(src).startsWith("data:image/")) {
    const tmp = new Image();
    tmp.onload = () => {
      const ratio = tmp.naturalHeight / tmp.naturalWidth;
      const slotW = slot.getBoundingClientRect().width || 320;

      let desired = Math.round(slotW * ratio);

      // safety limits
      const minH = 180;
      const maxH = Math.round(window.innerHeight * 0.6);
      desired = Math.max(minH, Math.min(maxH, desired));

      slot.style.height = desired + "px";
      slot.style.aspectRatio = "auto";
    };
    tmp.src = src;
  }
}

    if (cur) {
      if (cur.useCrop) cropBtn.classList.add("soc-on");

      const vp = document.createElement("div");
      vp.className = "soc-viewport";

      const img = document.createElement("img");
      img.src = (cur.useCrop && cur.cropSnapshot) ? cur.cropSnapshot : (cur.snapshot || cur.url);
      img.alt = cur.name || cat.name;

      vp.appendChild(img);
      slot.appendChild(vp);
    } else {
      const ph = document.createElement("div");
      ph.className = "soc-placeholder";
      ph.innerHTML = `${EMOJI[idx]} hinzufügen<br>Upload · Einfügen · URL`;
      slot.appendChild(ph);
    }

    // click activates slot
    slot.addEventListener("click", () => {
      state.activeSlot = idx;
      saveState();
      renderSlots();
    });

    // crop toggle
    cropBtn.addEventListener("click", async (e) => {
      e.stopPropagation();
      const current = cat.items[cat.active];
      if (!current) return;

      if (!current.useCrop) {
        if (!current.snapshot || !String(current.snapshot).startsWith("data:image/")) {
          alert("AutoCrop funktioniert zuverlässig für Upload/Paste (Screenshots). Bei externen Bild-URLs blockt der Browser oft den Zuschnitt.");
          return;
        }
        current.useCrop = true;

        // FORCE recrop (important when algorithm changed)
        current.cropSnapshot = null;

        const ok = await ensureCropped(current);

        if (!ok) {
          current.useCrop = false;
          alert("AutoCrop konnte hier nichts Sinnvolles erkennen.");
        }
      } else {
        current.useCrop = false;
      }

      saveState();
      renderSlots();
    });

    // delete
    trashBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (!cat.items.length) return;
      cat.items.splice(cat.active, 1);
      cat.active = clamp(cat.active - 1, 0, Math.max(0, cat.items.length - 1));
      saveState();
      renderSlots();
    });

    // Desktop arrows (click navigation)
    if (cat.items.length > 1) {
      const prev = document.createElement("button");
      prev.className = "soc-arrow soc-arrow-left";
      prev.type = "button";
      prev.innerHTML = "‹";
      prev.addEventListener("click", (e) => {
        e.stopPropagation();
        cat.active = (cat.active - 1 + cat.items.length) % cat.items.length;
        saveState();
        renderSlots();
      });

      const next = document.createElement("button");
      next.className = "soc-arrow soc-arrow-right";
      next.type = "button";
      next.innerHTML = "›";
      next.addEventListener("click", (e) => {
        e.stopPropagation();
        cat.active = (cat.active + 1) % cat.items.length;
        saveState();
        renderSlots();
      });

      slot.appendChild(prev);
      slot.appendChild(next);
    }

    // Mobile swipe left/right to switch images (keeps vertical scroll)
    let sx = 0, sy = 0, tracking = false, isHorizontal = false;
    const SWIPE_MIN = 45;

    slot.addEventListener("touchstart", (e) => {
      if (!e.touches || !e.touches[0]) return;
      tracking = true;
      isHorizontal = false;
      sx = e.touches[0].clientX;
      sy = e.touches[0].clientY;
    }, { passive: true });

    slot.addEventListener("touchmove", (e) => {
      if (!tracking || !e.touches || !e.touches[0]) return;
      const dx = e.touches[0].clientX - sx;
      const dy = e.touches[0].clientY - sy;

      if (!isHorizontal && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
        isHorizontal = Math.abs(dx) > Math.abs(dy);
      }
      if (isHorizontal) e.preventDefault();
    }, { passive: false });

    slot.addEventListener("touchend", (e) => {
      if (!tracking) return;
      tracking = false;
      if (!isHorizontal) return;
      if (!cat.items || cat.items.length <= 1) return;

      const t = (e.changedTouches && e.changedTouches[0]) ? e.changedTouches[0] : null;
      if (!t) return;

      const dx = t.clientX - sx;
      const dy = t.clientY - sy;
      if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy)) return;

      cat.active = dx < 0
        ? (cat.active + 1) % cat.items.length
        : (cat.active - 1 + cat.items.length) % cat.items.length;

      saveState();
      renderSlots();
    }, { passive: true });

    return slot;
  }

  function renderSlots() {
    applySlotHeight();
    const root = $("soc-slots");
    if (!root) return;
    root.innerHTML = "";
    state.categories.forEach((cat, idx) => root.appendChild(buildSlot(cat, idx)));
  }

  /* -------------------- iOS paste sheet -------------------- */
  function openPasteSheet() {
    if (document.getElementById("soc-paste-sheet")) return;

    const sheet = document.createElement("div");
    sheet.id = "soc-paste-sheet";
    sheet.innerHTML = `
      <div class="soc-ps-backdrop"></div>
      <div class="soc-ps-panel" role="dialog" aria-modal="true">
        <div class="soc-ps-title">Bild einfügen</div>
        <div class="soc-ps-hint">Tippe ins Feld → long-press → <b>Einfügen</b></div>
        <div id="soc-ps-input" class="soc-ps-input" contenteditable="true"></div>
        <div class="soc-ps-actions">
          <button id="soc-ps-close" class="soc-ps-btn" type="button">Schließen</button>
        </div>
      </div>
    `;
    document.body.appendChild(sheet);

    const close = () => sheet.remove();
    sheet.querySelector(".soc-ps-backdrop").addEventListener("click", close);
    sheet.querySelector("#soc-ps-close").addEventListener("click", close);

    const input = sheet.querySelector("#soc-ps-input");
    setTimeout(() => input.focus(), 80);

    input.addEventListener("paste", (e) => {
      try {
        const cd = e.clipboardData;
        if (!cd || !cd.items) return;

        for (const it of cd.items) {
          if (it.kind === "file" && it.type && it.type.startsWith("image/")) {
            const file = it.getAsFile();
            if (!file) continue;
            e.preventDefault();
            createItemFromFile(file).then((item) => {
              addItemToActiveSlot(item);
              close();
            });
            return;
          }
        }
      } catch (err) {}
    });
  }

  /* -------------------- bottom bar -------------------- */
  function setupBottomBar() {
    if ($("soc-bottombar")) return;

    const bar = document.createElement("div");
    bar.id = "soc-bottombar";
    bar.innerHTML = `
      <div class="soc-bb-row">
        <input id="soc-file" type="file" accept="image/*" hidden>
        <button class="soc-bb-btn soc-bb-primary" id="soc-upload" type="button">Upload</button>
        <button class="soc-bb-btn" id="soc-paste" type="button">Einfügen</button>
        <button class="soc-bb-btn" id="soc-url" type="button">URL</button>
      </div>
    `;
    document.body.appendChild(bar);

    const fileInput = $("soc-file");

    $("soc-upload").addEventListener("click", () => fileInput && fileInput.click());

    fileInput && fileInput.addEventListener("change", async () => {
      const file = fileInput.files && fileInput.files[0];
      if (!file) return;
      const item = await createItemFromFile(file);
      addItemToActiveSlot(item);
      fileInput.value = "";
    });

    $("soc-url").addEventListener("click", async () => {
      const url = prompt("Bild-URL einfügen (direkter Link .jpg/.png):");
      if (!url) return;
      const item = await createItemFromURL(url.trim());
      addItemToActiveSlot(item);
    });

    $("soc-paste").addEventListener("click", openPasteSheet);
  }

  /* -------------------- init -------------------- */
  async function init() {
    if (!$("soc-panel")) return;

    // critical IDs check (avoids silent failure)
    if (!$("soc-slots")) console.warn("Missing #soc-slots in Liquid");
    if (!$("soc-luca-results")) console.warn("Missing #soc-luca-results in Liquid");
    if (!$("soc-luca-preview")) console.warn("Missing #soc-luca-preview in Liquid");

    loadState();
    setupBottomBar();

    renderSlots();
    updateFitBtn();
    renderLucaPreview();

    $("soc-fit")?.addEventListener("click", toggleFit);

    $("soc-reset")?.addEventListener("click", () => {
      if (!confirm("Alles löschen?")) return;
      state.categories.forEach((c) => { c.items = []; c.active = 0; });
      state.luca.selected = null;
      state.ui.fitEnabled = false;
      state.ui.slotHeightPx = null;
      saveState();
      renderSlots();
      renderLucaPreview();
      updateFitBtn();
    });

    window.addEventListener("resize", () => {
      if (state.ui.fitEnabled) {
        state.ui.slotHeightPx = computeFitHeightPx();
        saveState();
        renderSlots();
      }
    });

    // LUCA list
    lucaItems = await fetchLucaVariants();
    renderLucaList();
    renderLucaPreview();
  }

  document.readyState === "loading"
    ? document.addEventListener("DOMContentLoaded", init)
    : init();
})();
